import React, { useContext, useState, useEffect } from 'react';
import { PageHeader } from '../../../PageHeader';
import { Button, Chip, Tab, Tabs, TextField, FormControl } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import { Site, SiteDto } from '../../../../dtos/site';
import { useApi } from '../../../useApi';
import { useRouterWithTypedParams } from '../../../../hooks/useRouterWithTypedParams';
import { AjaxResult } from '../../../../enums/ajaxResult';
import { useSnackbar } from 'notistack';
import { ValidationErrors } from '../../../shared/validationErrors';
import { TimeZone } from '../../../../enums/timeZone';
import { TimeZoneSelect } from '../TimeZoneSelect';
import { SiteCommissioningSummary } from '../../../../dtos/siteCommissioning';

import { DEVICE_MANAGEMENT_LOGIN_HOST_PATH } from "../../../../constants";
import { useAuth } from "../../../../features/auth/context";
import { DEFAULT_MAINTENANCE_WINDOW, EMPTY_MAINTENANCE_WINDOW } from "../consts";

import { HardwareConfigurationSummaryPanel } from "./HardwareConfigurationSummaryPanel";
import { MaintenanceWindowConfiguration } from "./MaintenanceWindowConfiguration";
import { DeviceActionsSection } from "./DeviceActionsSection";
import { SitePhotos } from "./SitePhotos";
import { CommissioningSection } from "./CommissioningSection";
import { EnableMaintenanceModeDialog } from "../EnableMaintenanceModeDialog";
import { ConsoleCard } from "./console/ConsoleCard";
import { SwitchRow } from "./console/SwitchRow";
import { PermissionNote } from "./console/PermissionNote";
import { HealthBadge } from "./console/HealthBadge";
import { CommissioningLinkButton } from "./console/CommissioningLinkButton";
import { tokens } from "./console/tokens";
import { useForceRerender } from "../../../../hooks/useForceRerender";
import SignalRContext from "../../../../signalr/context";

type ConfigTab = "details" | "hardware" | "photos";

interface HealthInfo {
    label: string;
    subtext: string;
    dotColor: string;
    dotBg: string;
}

export function siteHealth(summary: SiteCommissioningSummary | undefined, isOnline: boolean): HealthInfo {
    if (!summary || summary.sections.length === 0) {
        return {
            label: "Not yet commissioned",
            subtext: "This site hasn't sent any configuration data yet.",
            dotColor: tokens.off,
            dotBg: tokens.offBg,
        };
    }

    const sections = summary.sections.filter((s) => Object.keys(s.items).length > 0);

    if (sections.length === 0) {
        return {
            label: "Not yet commissioned",
            subtext: "This site hasn't sent any configuration data yet.",
            dotColor: tokens.off,
            dotBg: tokens.offBg,
        };
    }
    if (sections.some((s) => s.status === "Failed")) {
        return {
            label: "Attention needed",
            subtext: "One or more sections failed to apply on the device.",
            dotColor: tokens.danger,
            dotBg: tokens.dangerBg,
        };
    }
    if (!isOnline) {
        return {
            label: "Offline",
            subtext: "The device isn't currently connected.",
            dotColor: tokens.off,
            dotBg: tokens.offBg,
        };
    }
    if (sections.some((s) => s.status !== "Applied")) {
        return {
            label: "Sync pending",
            subtext: "The device hasn't confirmed applying the latest changes yet.",
            dotColor: tokens.wait,
            dotBg: tokens.waitBg,
        };
    }
    return {
        label: "Online & in sync",
        subtext: "The device is connected and every section is up to date.",
        dotColor: tokens.ok,
        dotBg: tokens.okBg,
    };
}

export const SiteConfigPage = () => {
    const classes = useStyles();
    const { isGlobalAdmin, isDealerAdmin, isDealerEmployee, isOrgAdmin, isSiteAdmin } = useAuth();
    const { enqueueSnackbar } = useSnackbar();
    const { match } = useRouterWithTypedParams<{ siteId?: number }>();
    const siteId = match.params.siteId;
    const { siteApi, maintenanceModeApi } = useApi();
    const { serviceStatus, maintenanceModeStatus } = useContext(SignalRContext);
    const [site, setSite] = useState<Site>();
    const [showEnableMaintenanceDialog, setShowEnableMaintenanceDialog] = useState(false);
    const [isInMaintenanceMode, setIsInMaintenanceMode] = useState(false);
    const [maintenanceModeRefreshTrigger, setMaintenanceModeRefreshTrigger] = useState(0);
    const [activeTab, setActiveTab] = useState<ConfigTab>("details");
    const [commissioningSummary, setCommissioningSummary] = useState<SiteCommissioningSummary>();
    useForceRerender(30000);

    useEffect(() => {
        if (!siteId) {
            return;
        }
        siteApi.getById(siteId).then((r) => {
            if (r.result === AjaxResult.Success && r.data) {
                setSite(r.data);
            }
        });
    }, [setSite, siteApi, siteId]);

    useEffect(() => {
        if (!siteId) {
            return;
        }
        siteApi.getCommissioning(siteId).then((r) => {
            if (r.result === AjaxResult.Success && r.data) {
                setCommissioningSummary(r.data);
            }
        });
    }, [siteApi, siteId]);

    useEffect(() => {
        if (!siteId || !commissioningSummary?.sections.some((s) => s.status === "Syncing")) {
            return;
        }
        const timer = setInterval(() => {
            siteApi.getCommissioning(siteId).then((r) => {
                if (r.result === AjaxResult.Success && r.data) {
                    setCommissioningSummary(r.data);
                }
            });
        }, 3000);
        return () => clearInterval(timer);
    }, [siteApi, siteId, commissioningSummary]);

    useEffect(() => {
        if (!siteId) {
            return;
        }
        maintenanceModeApi.getStatus(siteId).then((r) => {
            if (r.result === AjaxResult.Success && r.data) {
                setIsInMaintenanceMode(r.data.isInMaintenanceMode);
            }
        });
    }, [siteId, maintenanceModeApi, maintenanceModeRefreshTrigger]);

    useEffect(() => {
        if (!siteId) {
            return;
        }
        const signalRStatus = maintenanceModeStatus.get(siteId);
        if (signalRStatus !== undefined) {
            setIsInMaintenanceMode(signalRStatus.isEnabled);
        }
    }, [siteId, maintenanceModeStatus]);

    const [validationErrors, setValidationErrors] = useState<string[]>([]);
    const updateSite = () => {
        if (!siteId) {
            return;
        }
        siteApi.updateSite(siteId, { ...site } as SiteDto).then((r) => {
            if (r.result === AjaxResult.Success) {
                enqueueSnackbar("Successfully updated site configuration", { variant: "success" });
                setValidationErrors([]);
            } else if (r.result === AjaxResult.Duplicate) {
                enqueueSnackbar("Cannot use that Reference Site ID for this site, already used", { variant: "error" });
            } else if (r.messages) {
                setValidationErrors(r.messages);
            } else {
                enqueueSnackbar("Failed to update configuration for site", { variant: "error" });
            }
        });
    };

    const isSiteManager = isGlobalAdmin || isDealerAdmin;
    const showDeviceActionsSection = isSiteManager;
    const showCommissioningSiteLink = isSiteManager || isDealerEmployee;
    const canViewSiteConfiguration = !isOrgAdmin && !isSiteAdmin;

    const loadSite = () => {
        if (!siteId) {
            return;
        }
        siteApi.getById(siteId).then((r) => {
            if (r.result === AjaxResult.Success && r.data) {
                setSite(r.data);
            }
        });
    };

    const handleMaintenanceModeSuccess = () => {
        loadSite();
        setMaintenanceModeRefreshTrigger(prev => prev + 1);
    };

    const isSiteOnline = Boolean(siteId && serviceStatus.get(siteId));
    const health = siteHealth(commissioningSummary, isSiteOnline);
    const hardwareCount = commissioningSummary
        ? commissioningSummary.sections.filter((s) => Object.keys(s.items).length > 0).length
        : undefined;

    return (
        <div className={classes.page}>
            <PageHeader>
                <span className={classes.headingRow}>
                    Site Configuration: {site && site.name}
                    {showCommissioningSiteLink && site && site.balenaCloudDeviceId && (
                        <CommissioningLinkButton
                            url={`https://${site.balenaCloudDeviceId}.${DEVICE_MANAGEMENT_LOGIN_HOST_PATH}`}
                            enabled={isInMaintenanceMode}
                        />
                    )}
                </span>
            </PageHeader>

            <div className={classes.contextRow}>
                <div />
                <HealthBadge label={health.label} subtext={health.subtext} dotColor={health.dotColor} dotBg={health.dotBg} />
            </div>

            <Tabs
                value={activeTab}
                onChange={(_, value: ConfigTab) => setActiveTab(value)}
                indicatorColor="primary"
                textColor="primary"
                className={classes.tabs}
            >
                <Tab label="Site Details" value="details" />
                <Tab
                    label={hardwareCount !== undefined ? (
                        <span className={classes.tabLabel}>
                            Hardware <Chip size="small" label={hardwareCount} className={classes.tabCount} />
                        </span>
                    ) : "Hardware"}
                    value="hardware"
                />
                <Tab label="Photos" value="photos" />
            </Tabs>

            {activeTab === "details" && (
                <>
                    <ConsoleCard
                        title="Site details"
                        subtitle="Identity information for this location. Managed entirely in the portal — nothing here reaches the device."
                    >
                        <div className={classes.grid2}>
                            <div>
                                <label className={classes.fieldLabel}>Site name</label>
                                <TextField
                                    fullWidth
                                    variant="outlined"
                                    value={site ? site.name : ''}
                                    onChange={(event) => {
                                        if (site !== undefined) {
                                            setSite({ ...site, name: event.currentTarget.value });
                                        }
                                    }}
                                />
                            </div>
                            <div>
                                <label className={classes.fieldLabel}>Reference site ID</label>
                                <TextField
                                    fullWidth
                                    variant="outlined"
                                    value={site ? site.referenceSiteId : ''}
                                    onChange={(event) => {
                                        if (site !== undefined) {
                                            setSite({ ...site, referenceSiteId: event.currentTarget.value });
                                        }
                                    }}
                                />
                            </div>
                            <div>
                                <label className={classes.fieldLabel}>Time zone</label>
                                <FormControl fullWidth variant="outlined">
                                    <TimeZoneSelect
                                        value={site ? site.timeZone as TimeZone : TimeZone.UTC}
                                        onChange={(timeZone) => {
                                            if (site === undefined) return;
                                            setSite({ ...site, timeZone });
                                        }}
                                    />
                                </FormControl>
                            </div>
                            {(isGlobalAdmin || isDealerAdmin) && (
                                <div>
                                    <label className={classes.fieldLabel}>Site notes</label>
                                    <TextField
                                        fullWidth
                                        variant="outlined"
                                        placeholder="Nothing on file"
                                        value={site ? site.notes || "" : ""}
                                        onChange={(event) => {
                                            if (site !== undefined) {
                                                setSite({ ...site, notes: event.currentTarget.value });
                                            }
                                        }}
                                    />
                                </div>
                            )}
                        </div>
                    </ConsoleCard>

                    {canViewSiteConfiguration && (
                        <div className={classes.hardwareSummarySection}>
                            <HardwareConfigurationSummaryPanel summary={commissioningSummary} defaultExpanded />
                        </div>
                    )}

                    <ConsoleCard title="Session & billing controls">
                        {site && (isGlobalAdmin || isDealerAdmin) && (
                            <SwitchRow
                                label="Automatic session abandonment"
                                description="Ends an idle room session automatically in 3 hours instead of waiting for a manual override."
                                checked={site.siteConfiguration ? site.siteConfiguration.automaticSessionAbandonment : false}
                                onChange={(checked) => setSite({
                                    ...site,
                                    siteConfiguration: { ...site.siteConfiguration, automaticSessionAbandonment: checked },
                                })}
                            />
                        )}
                        <SwitchRow
                            label="Exclude from billing"
                            description="Marks this as a demo or internal site."
                            checked={site ? site.excludeFromBilling : false}
                            onChange={(checked) => {
                                if (site !== undefined) {
                                    setSite({ ...site, excludeFromBilling: checked });
                                }
                            }}
                            disabled={!isGlobalAdmin}
                        />
                        {site && isGlobalAdmin && (
                            <>
                                <SwitchRow
                                    label="Maintenance window"
                                    description="Suppresses alerting during a recurring scheduled window."
                                    checked={Boolean(site.maintenanceWindow)}
                                    onChange={(checked) => setSite({
                                        ...site,
                                        maintenanceWindow: checked ? DEFAULT_MAINTENANCE_WINDOW : null,
                                    })}
                                />
                                <MaintenanceWindowConfiguration
                                    configuration={site.maintenanceWindow || EMPTY_MAINTENANCE_WINDOW}
                                    setConfiguration={(conf) => setSite({ ...site, maintenanceWindow: conf })}
                                    disabled={!site.maintenanceWindow}
                                />
                            </>
                        )}
                        {!isSiteManager && (
                            <PermissionNote>
                                Some settings on this page are hidden or read-only for your account.
                            </PermissionNote>
                        )}
                    </ConsoleCard>

                    <div className={classes.saveBar}>
                        <Button color="primary" variant="contained" onClick={updateSite}>
                            Save changes
                        </Button>
                        {validationErrors.length > 0 && <ValidationErrors errors={validationErrors} />}
                    </div>
                </>
            )}

            {activeTab === "hardware" && (
                <>
                    {canViewSiteConfiguration && commissioningSummary && (
                        <CommissioningSection
                            summary={commissioningSummary}
                            siteId={siteId}
                            canEdit={isSiteManager}
                            onSummaryUpdated={setCommissioningSummary}
                        />
                    )}
                    {site && (
                        <DeviceActionsSection
                            site={site}
                            showDangerousButtons={showDeviceActionsSection}
                            isSiteOnline={isSiteOnline}
                            onEnableMaintenanceMode={() => setShowEnableMaintenanceDialog(true)}
                        />
                    )}
                </>
            )}

            {activeTab === "photos" && site && <SitePhotos siteId={site.id} />}

            {siteId && (
                <EnableMaintenanceModeDialog
                    open={showEnableMaintenanceDialog}
                    onClose={() => setShowEnableMaintenanceDialog(false)}
                    siteId={siteId}
                    onSuccess={handleMaintenanceModeSuccess}
                />
            )}
        </div>
    );
};

const useStyles = makeStyles({
    page: {
        maxWidth: "1180px",
    },
    headingRow: {
        display: "inline-flex",
        alignItems: "center",
        gap: "10px",
    },
    contextRow: {
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
        margin: "14px 0",
    },
    tabs: {
        marginBottom: "26px",
        borderBottom: `1px solid ${tokens.line}`,
    },
    tabLabel: {
        display: "flex",
        alignItems: "center",
        gap: "6px",
    },
    tabCount: {
        height: "18px",
        fontSize: "10.5px",
        fontWeight: 600,
        background: tokens.offBg,
        color: tokens.hint,
    },
    grid2: {
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: "20px 28px",
        "@media (max-width: 760px)": {
            gridTemplateColumns: "1fr",
        },
    },
    fieldLabel: {
        display: "block",
        fontSize: "12px",
        fontWeight: 500,
        color: tokens.muted,
        textTransform: "uppercase",
        letterSpacing: "0.045em",
        marginBottom: "7px",
    },
    hardwareSummarySection: {
        marginBottom: "20px",
    },
    saveBar: {
        display: "flex",
        alignItems: "center",
        gap: "14px",
        marginTop: "24px",
    },
});
